"use client"

import { ArrowTopRightOnSquareIcon } from "@heroicons/react/16/solid"
import { ArrowUturnLeftIcon } from "@heroicons/react/20/solid"
import AssetComboIcon from "@src/components/DefuseSDK/components/Asset/AssetComboIcon"
import { useMachineStageProgress } from "@src/components/DefuseSDK/features/common/useMachineStageProgress"
import {
  getStageFromState,
  is1csError,
  is1csSuccess,
  isIntentError,
  isIntentSuccess,
  SWAP_STAGE_LABELS,
  SWAP_STAGES,
  type SwapStage,
} from "@src/components/DefuseSDK/features/swap/utils/swapStatusUtils"
import type { TokenUsdPriceData } from "@src/components/DefuseSDK/hooks/useTokensUsdPrices"
import type { TokenValuePresentations } from "@src/components/DefuseSDK/types/asset"
import type { TokenInfo, TokenValue } from "@src/components/DefuseSDK/types/base"
import {
  formatTokenValue,
  formatUsdAmount,
} from "@src/components/DefuseSDK/utils/format"
import { ProgressSteps } from "@src/components/ProgressIndicator"
import { ArrowDownIcon } from "@src/icons"
import type { TrackedSwapIntent } from "@src/providers/SwapTrackerMachineProvider"
import Button from "./Button"
import { buildIntentsExplorerTxUrl } from "./DefuseSDK/constants/blockchains"
import { useTokensUsdPrices } from "./DefuseSDK/hooks/useTokensUsdPrices"
import getTokenUsdPrice from "./DefuseSDK/utils/getTokenUsdPrice"
import {
  getTokenValuePresentation,
  presentTokenAmount,
} from "./DefuseSDK/utils/tokenValue"

type SwapStatusProps = {
  swap: TrackedSwapIntent
  variant: "full" | "dock"
  tokenPriceOverrides?: Record<string, number>
  valuePresentations?: TokenValuePresentations
  onSwapAgain?: () => void
}

export function SwapStatus({
  swap,
  variant,
  tokenPriceOverrides,
  valuePresentations,
  onSwapAgain,
}: SwapStatusProps) {
  const { displayStage, displayIndex, canRetry, stateValue, contextStatus } =
    useMachineStageProgress({
      actorRef: swap.actorRef,
      stages: SWAP_STAGES,
      getStageFromState,
    })

  const isError = swap.is1cs
    ? is1csError(contextStatus)
    : isIntentError(stateValue)

  const isSuccess = swap.is1cs
    ? is1csSuccess(contextStatus)
    : isIntentSuccess(stateValue)

  if (variant === "dock") {
    return (
      <ProgressSteps
        stages={SWAP_STAGES}
        stageLabels={SWAP_STAGE_LABELS}
        displayStage={displayStage}
        displayIndex={displayIndex}
        isError={isError}
        isSuccess={isSuccess}
        size="sm"
      />
    )
  }

  return (
    <FullView
      swap={swap}
      depositAddress={swap.depositAddress}
      displayStage={displayStage}
      displayIndex={displayIndex}
      isError={isError}
      canRetry={canRetry}
      isSuccess={isSuccess}
      tokenPriceOverrides={tokenPriceOverrides}
      valuePresentations={valuePresentations}
      onSwapAgain={onSwapAgain}
    />
  )
}

function FullView({
  swap,
  depositAddress,
  displayStage,
  displayIndex,
  isError,
  canRetry,
  isSuccess,
  tokenPriceOverrides,
  valuePresentations,
  onSwapAgain,
}: {
  swap: TrackedSwapIntent
  depositAddress: string | undefined
  displayStage: SwapStage
  displayIndex: number
  isError: boolean
  canRetry: boolean
  isSuccess: boolean
  tokenPriceOverrides?: Record<string, number>
  valuePresentations?: TokenValuePresentations
  onSwapAgain?: () => void
}) {
  const { tokenIn, tokenOut, totalAmountIn, totalAmountOut } = swap
  const { data: tokensUsdPriceData } = useTokensUsdPrices()

  const amountInDisplay = formatSwapStatusLeg(
    tokenIn,
    totalAmountIn,
    tokensUsdPriceData,
    tokenPriceOverrides,
    valuePresentations
  )
  const amountOutDisplay = formatSwapStatusLeg(
    tokenOut,
    totalAmountOut,
    tokensUsdPriceData,
    tokenPriceOverrides,
    valuePresentations
  )

  const explorerUrl = depositAddress
    ? buildIntentsExplorerTxUrl(depositAddress)
    : null

  return (
    <>
      <div className="mt-7 rounded-3xl border border-gray-200 bg-white p-5 sm:p-6 dark:border-white/5 dark:bg-gray-800">
        <div className="flex flex-col">
          <div className="flex items-center gap-3">
            <AssetComboIcon icon={tokenIn?.icon} sizeClassName="size-11" />
            <div>
              <div className="font-bold text-2xl text-gray-900 leading-7 tracking-tight dark:text-white">
                {amountInDisplay.amount} {amountInDisplay.symbol}
              </div>
              <div className="font-medium text-base/5 text-gray-500 dark:text-gray-400">
                {formatUsdAmount(amountInDisplay.usd ?? 0)}
              </div>
            </div>
          </div>

          <div
            className="flex h-16 w-11 items-center justify-center"
            aria-hidden
          >
            <ArrowDownIcon className="size-7 text-gray-400" />
          </div>

          <div className="flex items-center gap-3">
            <AssetComboIcon icon={tokenOut?.icon} sizeClassName="size-11" />
            <div>
              <div className="font-bold text-2xl text-gray-900 leading-7 tracking-tight dark:text-white">
                {amountOutDisplay.amount} {amountOutDisplay.symbol}
              </div>
              <div className="font-medium text-base/5 text-gray-500 dark:text-gray-400">
                {formatUsdAmount(amountOutDisplay.usd ?? 0)}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 border-gray-200 border-t pt-6 sm:mt-8 sm:pt-8 dark:border-white/10">
          <ProgressSteps
            stages={SWAP_STAGES}
            stageLabels={SWAP_STAGE_LABELS}
            displayStage={displayStage}
            displayIndex={displayIndex}
            isError={isError}
            isSuccess={isSuccess}
            size="md"
          />
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3">
        {explorerUrl && !swap.isConfidential && (
          <Button
            href={explorerUrl}
            variant="secondary"
            size="xl"
            target="_blank"
            rel="noopener noreferrer"
            fullWidth
          >
            View on explorer
            <ArrowTopRightOnSquareIcon className="size-4" />
          </Button>
        )}
        {isSuccess && (
          <Button size="xl" fullWidth onClick={onSwapAgain}>
            <ArrowUturnLeftIcon className="size-4.5 shrink-0" />
            Swap again
          </Button>
        )}
        {isError && canRetry && (
          <Button
            size="xl"
            fullWidth
            onClick={() => swap.actorRef.send({ type: "RETRY" })}
          >
            Retry
          </Button>
        )}
        {isError && (
          <Button size="xl" variant="secondary" fullWidth onClick={onSwapAgain}>
            Try a new swap
          </Button>
        )}
      </div>
    </>
  )
}

export function formatSwapStatusLeg(
  token: TokenInfo,
  amount: TokenValue,
  tokensUsdPriceData: TokenUsdPriceData | undefined,
  tokenPriceOverrides?: Record<string, number>,
  valuePresentations?: TokenValuePresentations
): { amount: string; symbol: string; usd: number | null } {
  const shareAmount = formatTokenValue(amount.amount, amount.decimals, {
    min: 0.0001,
    fractionDigits: 4,
  })
  const usd = getTokenUsdPrice(
    shareAmount,
    token,
    tokensUsdPriceData,
    tokenPriceOverrides
  )

  const presentation = getTokenValuePresentation(token, valuePresentations)

  const presentedAmount = presentTokenAmount(shareAmount, token, presentation)

  return {
    amount: presentedAmount.amount,
    symbol: presentedAmount.symbol,
    usd,
  }
}
